import type { CityBenefit, NearbyCityZip, NearbyCityLink, Landmark } from "../types";

export default function CityLocalAreaSection({
  city,
  state,
  cities,
  benefits,
  internalLinks,
  officeNote,
  officeDistance,
  officeAddress,
  serviceAreaIntro,
  nearbyCitiesIntro,
  localAreaIntro,
  mapEmbedUrl,
  officePhone,
  officeHours,
  officePhotoUrl,
  officePhotoAlt,
  landmarks,
  exploreNearbyHeading,
}: {
  city: string;
  state: string;
  cities: NearbyCityZip[];
  benefits: CityBenefit[];
  internalLinks: NearbyCityLink[];
  officeNote: string;
  officeDistance: string;
  officeAddress?: string;
  serviceAreaIntro: string;
  nearbyCitiesIntro: string;
  localAreaIntro?: string[];
  mapEmbedUrl?: string;
  officePhone?: string;
  officeHours?: { days: string; hours: string }[];
  officePhotoUrl?: string;
  officePhotoAlt?: string;
  landmarks?: Landmark[];
  exploreNearbyHeading?: string;
}) {
  const effectiveMapUrl =
    mapEmbedUrl ||
    (officeAddress
      ? `https://maps.google.com/maps?q=${encodeURIComponent(officeAddress)}&z=14&output=embed`
      : undefined);

  const nearbyPillsLabel = exploreNearbyHeading || "Explore Nearby City Pages";

  return (
    <section id="service-area" className="bg-white pt-8 md:pt-10 pb-20 md:pb-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving {city} &amp; Nearby {state}
          </h2>
          {serviceAreaIntro && (
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
              {serviceAreaIntro}
            </p>
          )}
        </div>

        {localAreaIntro && localAreaIntro.length > 0 && (
          <div className="max-w-4xl mx-auto mb-16">
            <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
              {localAreaIntro.map((paragraph, idx) => (
                <p key={idx} className="text-gray-600 text-base leading-[1.8] mb-5 last:mb-0">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office Info */}
          <div>
            <div className="bg-[#1A365D] rounded-lg p-7 text-white">
              <div className="flex flex-col xl:flex-row gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                      <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                    </div>
                    <div>
                      <p className="font-bold text-base">Fast Passport Center — {city}, {state}</p>
                      {officeAddress && (
                        <>
                          <p className="text-white/60 text-xs mt-0.5">{officeAddress}</p>
                          <a
                            href={`https://maps.google.com/maps?daddr=${encodeURIComponent(officeAddress)}`}
                            target="_blank"
                            rel="nofollow noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[#F6C344] hover:text-white text-xs font-semibold mt-1 transition-colors"
                          >
                            <i className="ri-navigation-line"></i> Get Driving Directions on Google Maps
                          </a>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Phone Number */}
                  {officePhone && (
                    <div className="flex items-start gap-2.5 mb-3 ml-[52px]">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0 mt-0.5">
                        <i className="ri-phone-line text-[#F6C344] text-xs"></i>
                      </div>
                      <div>
                        <a href={`tel:${officePhone.replace(/\D/g, "")}`} className="text-white/80 text-sm hover:text-[#F6C344] transition-colors">{officePhone}</a>
                        <p className="text-white/40 text-xs">Nationwide toll-free — 24/7 voicemail</p>
                      </div>
                    </div>
                  )}

                  {/* Office Hours */}
                  {officeHours && officeHours.length > 0 && (
                    <div className="flex items-start gap-2.5 mb-4 ml-[52px]">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0 mt-0.5">
                        <i className="ri-time-line text-[#F6C344] text-xs"></i>
                      </div>
                      <div>
                        <div className="text-white/80 text-sm space-y-0.5">
                          {officeHours.map((h, i) => (
                            <p key={i}>
                              {h.days}: {h.hours}
                            </p>
                          ))}
                        </div>
                        <p className="text-white/40 text-xs mt-1 italic">After-hours emergency service available by phone</p>
                      </div>
                    </div>
                  )}

                  <p className="text-white/70 text-sm mb-1">
                    <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                    {officeDistance}
                  </p>
                  <p className="text-white/50 text-xs mb-4 ml-5">Courier pickup available throughout the {city} area</p>

                  {/* Office Photo */}
                  {officePhotoUrl && (
                    <div className="mb-5 rounded-md overflow-hidden border border-white/10">
                      <img
                        src={officePhotoUrl}
                        alt={officePhotoAlt || `Fast Passport Center office in ${city}, ${state}`}
                        className="w-full h-auto object-cover object-top"
                      />
                      {officeAddress && (
                        <div className="px-3 py-2 bg-white/5">
                          <p className="text-white/40 text-xs italic">{officeAddress}</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="border-t border-white/10 pt-5">
                    <p className="text-white/80 text-sm leading-relaxed mb-4">{officeNote}</p>
                    <a
                      href="https://fastpassportcenter.com/checkout/"
                      className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                    >
                      Start My Passport <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>

                {/* Map */}
                {effectiveMapUrl && (
                  <div className="xl:w-[340px] flex-shrink-0">
                    <div className="rounded-lg overflow-hidden border border-white/10" style={{ height: "260px" }}>
                      <iframe
                        src={effectiveMapUrl}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title={`Fast Passport Center ${city} Office Location`}
                        aria-label={`Google Map showing Fast Passport Center ${city} office`}
                      />
                    </div>
                    {officeAddress && (
                      <p className="text-white/40 text-xs mt-2 text-center">{officeAddress}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right — Cities + Nearby Links + Benefit Tiles + Trust + Landmarks */}
          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities &amp; Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((cityItem, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md">
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{cityItem.name}</span>
                    </div>
                    {cityItem.zips && (
                      <span className="text-gray-400 text-xs font-mono">{cityItem.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">{nearbyCitiesIntro}</p>
              {internalLinks && internalLinks.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">{nearbyPillsLabel}</p>
                  <div className="flex flex-wrap gap-2">
                    {internalLinks.map((link) => (
                      <a
                        key={link.route}
                        href={link.route}
                        className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                      >
                        {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Benefit Tiles */}
            {benefits && benefits.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                {benefits.map((b, i) => (
                  <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                      <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                    </div>
                    <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Trust Credentials Strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Nearby Landmarks & Airports */}
            {landmarks && landmarks.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#F6C344]/15">
                    <i className="ri-landscape-line text-[#1A365D] text-xs"></i>
                  </div>
                  <h3 className="font-semibold text-[#1A365D] text-sm uppercase tracking-wide">Nearby Landmarks &amp; Airports</h3>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {landmarks.map((item) => (
                    <span
                      key={item.name}
                      className="inline-flex items-center gap-1.5 bg-[#F7F8FA] border border-gray-200/80 rounded-full px-3 py-1.5 hover:border-[#F6C344] transition-colors cursor-default whitespace-nowrap"
                      title={item.desc}
                    >
                      <i className={`${item.icon} text-[#1A365D] text-[10px]`}></i>
                      <span className="text-[#1A365D] font-medium text-[11px]">{item.name}</span>
                      <span className="text-[#F6C344] font-semibold text-[10px]">{item.distance}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}